const path = (stage, d, w = 3) => ({ stage, d, w });
const ellipse = (x, y, rx, ry) => `M${x - rx},${y} a${rx},${ry} 0 1,0 ${rx * 2},0 a${rx},${ry} 0 1,0 ${-rx * 2},0`;
/** Original, monochrome symbols. All meaningful differences are visible without color. */
function icon(kind, x, y, size, stage) {
    const p = (a, b) => `${x + a * size},${y + b * size}`;
    const line = (d) => path(stage, d);
    switch (kind) {
        case 'circle':
            return [line(ellipse(x, y, size, size))];
        case 'triangle':
            return [line(`M${p(0, -1)} L${p(1, 1)} L${p(-1, 1)} Z`)];
        case 'star': {
            const points = Array.from({ length: 10 }, (_, i) => {
                const a = -Math.PI / 2 + (i * Math.PI) / 5;
                const r = i % 2 ? 0.44 : 1;
                return p(Math.cos(a) * r, Math.sin(a) * r);
            });
            return [line(`M${points.join(' L')} Z`)];
        }
        case 'heart':
            return [
                line(`M${p(0, 1)} C${p(-2, -0.3)} ${p(-0.7, -1.7)} ${p(0, -0.5)} C${p(0.7, -1.7)} ${p(2, -0.3)} ${p(0, 1)} Z`),
            ];
        case 'apple':
            return [
                line(`M${p(0, -0.65)} C${p(-1.5, -1.4)} ${p(-1.2, 1.2)} ${p(0, 0.9)} C${p(1.2, 1.2)} ${p(1.5, -1.4)} ${p(0, -0.65)} Z`),
                line(`M${p(0, -0.65)} Q${p(-0.1, -1.15)} ${p(0.25, -1.3)}`),
                line(`M${p(0, -0.85)} Q${p(0.9, -1.5)} ${p(0.8, -0.85)} Q${p(0.5, -0.55)} ${p(0, -0.85)}`),
            ];
        case 'pear':
            return [
                line(`M${p(0, -1)} C${p(-0.6, -1)} ${p(-0.3, -0.4)} ${p(-0.75, 0)} C${p(-1.6, 1.5)} ${p(1.6, 1.5)} ${p(0.75, 0)} C${p(0.3, -0.4)} ${p(0.6, -1)} ${p(0, -1)} Z`),
                line(`M${p(0, -1)} Q${p(0, -1.4)} ${p(0.25, -1.5)}`),
            ];
        case 'bottle':
            return [
                line(`M${p(-0.25, -1.2)} L${p(0.25, -1.2)} L${p(0.25, -0.65)} Q${p(0.7, -0.4)} ${p(0.7, -0.1)} L${p(0.7, 1)} L${p(-0.7, 1)} L${p(-0.7, -0.1)} Q${p(-0.7, -0.4)} ${p(-0.25, -0.65)} Z`),
                line(`M${p(-0.7, 0.15)} L${p(0.7, 0.15)} M${p(-0.7, 0.7)} L${p(0.7, 0.7)}`),
            ];
        case 'cup':
            return [
                line(`M${p(-0.85, -0.7)} L${p(0.65, -0.7)} L${p(0.5, 0.9)} Q${p(-0.1, 1.2)} ${p(-0.7, 0.9)} Z`),
                line(`M${p(0.65, -0.4)} C${p(1.65, -0.6)} ${p(1.6, 0.85)} ${p(0.6, 0.65)}`),
            ];
        case 'moon':
            return [
                line(`M${p(0.45, -1)} C${p(-1.5, -0.8)} ${p(-1.5, 1.1)} ${p(0.6, 1)} C${p(-0.3, 0.5)} ${p(-0.3, -0.4)} ${p(0.45, -1)} Z`),
            ];
        case 'flower':
            return [
                ...Array.from({ length: 5 }, (_, i) => {
                    const a = -Math.PI / 2 + (i * Math.PI * 2) / 5;
                    return line(ellipse(x + Math.cos(a) * size * 0.58, y + Math.sin(a) * size * 0.58, size * 0.4, size * 0.4));
                }),
                line(ellipse(x, y, size * 0.23, size * 0.23)),
            ];
    }
}
function set(id, scene, axes, text, render) {
    return {
        id: `pack-${id}`,
        level: 1,
        scene,
        axes,
        source: 'pack',
        render,
        sentences: axes[0].values.flatMap((a) => axes[1].values.map((b) => ({
            text: text(a, b),
            features: { [axes[0].name]: a, [axes[1].name]: b },
        }))),
    };
}
const axis = (name, a, b) => ({
    name,
    values: [a, b],
    drawHint: `Clearly distinguish ${a} from ${b}.`,
});
const pairs = [
    ['apple', 'pear'],
    ['cup', 'bottle'],
    ['star', 'heart'],
];
const symbols = [
    ['star', 'heart'],
    ['circle', 'triangle'],
    ['flower', 'moon'],
];
export const EXTRA_PACK_SETS = [
    ...pairs.map(([a, b], i) => set(`table-${i + 1}`, 'An object on or under a small table.', [axis('place', 'on', 'under'), axis('object', a, b)], (place, object) => `The ${object} is ${place} the table.`, (f) => {
        const y = f.place === 'on' ? 155 : 286;
        return [
            path(1, 'M80,205 L320,205 L320,222 L80,222 Z M100,222 L100,345 M300,222 L300,345'),
            path(2, ellipse(200, y + 39, 45, 5), 2),
            ...icon(f.object, 200, y, 33, 3),
            path(4, 'M50,350 L350,350 M92,213 L310,213', 1.6),
        ];
    })),
    ...symbols.map(([a, b], i) => set(`shelf-${i + 1}`, 'A decorative object on one of two shelves.', [axis('shelf', 'top', 'bottom'), axis('object', a, b)], (shelf, object) => `The ${object} is on the ${shelf} shelf.`, (f) => {
        const y = f.shelf === 'top' ? 128 : 258;
        return [
            path(1, 'M70,60 L70,340 L330,340 L330,60 Z M70,180 L330,180 M70,310 L330,310'),
            path(2, ellipse(200, y + 43, 42, 5), 2),
            ...icon(f.object, 200, y, 36, 3),
            path(4, 'M80,190 L105,190 L105,225 M295,320 L320,320 M80,70 L80,330', 1.6),
        ];
    })),
    ...['round', 'heart-shaped', 'star-shaped'].map((shape, i, all) => set(`balloons-${i + 1}`, 'Balloons tied to a small gift box.', [axis('count', 'one', 'two'), axis('shape', shape, all[(i + 1) % all.length])], (count, form) => count === 'one'
        ? `There is one ${form} balloon above the gift.`
        : `There are two ${form} balloons above the gift.`, (f) => {
        const xs = f.count === 'one' ? [200] : [130, 270];
        const kind = f.shape === 'round' ? 'circle' : f.shape === 'heart-shaped' ? 'heart' : 'star';
        return [
            path(1, 'M150,280 L250,280 L250,345 L150,345 Z M142,280 L258,280 L258,295 L142,295 Z'),
            ...xs.map((x) => path(2, `M200,280 Q${x - 20},240 ${x},190 M${x},180 L${x - 5},190 L${x + 5},190 Z`, 2)),
            ...xs.flatMap((x) => icon(kind, x, 130, 48, 3)),
            path(4, 'M192,280 L192,345 M208,280 L208,345 M200,280 C140,270 180,245 200,280 C220,245 260,270 200,280', 2),
        ];
    })),
    ...symbols.map(([a, b], i) => set(`sign-${i + 1}`, 'An arrow sign with a symbol, standing beside a path.', [axis('direction', 'left', 'right'), axis('symbol', a, b)], (direction, symbol) => `The sign with a ${symbol} points to the ${direction}.`, (f) => [
        path(1, 'M192,210 L192,335 M208,210 L208,335 M80,345 Q200,305 320,345'),
        path(2, f.direction === 'left'
            ? 'M70,155 L130,100 L325,100 L325,210 L130,210 Z'
            : 'M330,155 L270,100 L75,100 L75,210 L270,210 Z'),
        ...icon(f.symbol, 200, 155, 33, 3),
        path(4, 'M193,235 L201,250 M190,328 L180,341 M270,339 L279,325 L286,341', 1.6),
    ])),
    ...[
        ['apple', 'pear'],
        ['star', 'heart'],
        ['circle', 'triangle'],
    ].map(([a, b], i) => set(`basket-${i + 1}`, 'One or two objects in a shallow basket.', [axis('count', 'one', 'two'), axis('object', a, b)], (count, object) => `There ${count === 'one' ? 'is one' : 'are two'} ${object}${count === 'one' ? '' : 's'} in the basket.`, (f) => {
        const xs = f.count === 'one' ? [200] : [145, 255];
        return [
            path(1, 'M80,225 L110,335 L290,335 L320,225 M75,225 L325,225 M90,225 C80,95 320,95 310,225'),
            ...xs.map((x) => path(2, ellipse(x, 307, 34, 4), 1.6)),
            ...xs.flatMap((x) => icon(f.object, x, 265, 36, 3)),
            path(4, 'M108,323 L292,323 M128,317 L136,333 M173,317 L177,333 M223,317 L223,333 M270,317 L265,333', 1.6),
        ];
    })),
    ...['star', 'heart', 'circle'].map((symbol, i) => set(`flag-${i + 1}`, 'A flag on a pole, with one or two symbols.', [axis('shape', 'triangular', 'rectangular'), axis('count', 'one', 'two')], (shape, count) => `The ${shape} flag has ${count} ${symbol}${count === 'one' ? '' : 's'}.`, (f) => [
        path(1, 'M92,55 L92,350 M82,350 L112,350'),
        path(2, f.shape === 'triangular'
            ? 'M97,75 L340,150 L97,225 Z'
            : 'M97,75 L340,75 L340,225 L97,225 Z'),
        ...(f.count === 'one' ? [168] : [150, 225]).flatMap((x) => icon(symbol, x, 150, 19, 3)),
        path(4, 'M75,360 L340,360 M95,58 L103,58 M97,235 L115,235', 1.6),
    ])),
    ...['star', 'cloud', 'bird'].map((object, i) => set(`sky-${i + 1}`, 'A night sky above a quiet hill.', [axis('moon', 'full', 'crescent'), axis('count', 'one', 'two')], (moon, count) => `There ${count === 'one' ? 'is one' : 'are two'} ${object}${count === 'one' ? '' : 's'} beside the ${moon} moon.`, (f) => {
        const ys = f.count === 'one' ? [170] : [120, 220];
        return [
            path(1, 'M30,335 Q120,275 200,330 Q290,285 370,340'),
            ...icon(f.moon === 'full' ? 'circle' : 'moon', 130, 160, 54, 2),
            ...ys.flatMap((y) => object === 'star'
                ? icon('star', 275, y, 25, 3)
                : [
                    path(3, object === 'cloud'
                        ? `M235,${y} C220,${y - 15} 240,${y - 35} 252,${y - 25} C260,${y - 50} 295,${y - 43} 298,${y - 22} C330,${y - 28} 335,${y} 312,${y} Z`
                        : `M242,${y - 15} Q263,${y - 25} 278,${y} Q292,${y - 25} 315,${y - 15}`),
                ]),
            path(4, 'M100,322 L105,302 L110,320 M290,320 L295,303 L300,321', 1.6),
        ];
    })),
];
//# sourceMappingURL=extra-pack.js.map